/**
 * Index spatial de la trace de calcul — EF-CAL-011.
 *
 * Grille uniforme de 1 km de côté, indexée depuis une projection
 * équirectangulaire ancrée au centre du rectangle englobant du parcours.
 *
 * L'exhaustivité est la seule propriété obligatoire : l'index ne doit jamais
 * omettre un point situé dans le rayon. Il peut en retourner davantage, les
 * distances exactes étant de toute façon recalculées selon EF-CAL-010 sur les
 * candidats retournés. C'est cette séparation entre sélection approchée et
 * calcul exact qui autorise un index géométriquement grossier sans dégrader
 * aucune valeur affichée.
 */

import { DEG_TO_RAD } from '../core/units';
import type { Coordinate } from './geodesy';
import type { ComputedTrack } from './track';

/** Côté d'une cellule, en mètres. */
export const CELL_SIZE_METERS = 1000;

/**
 * Marge, en cellules, absorbant la distorsion de la projection sur un parcours
 * étendu et garantissant l'exhaustivité.
 */
export const CELL_MARGIN = 2;

/** Mètres par degré de latitude, valeur de référence de la projection. */
const METERS_PER_DEGREE = 111320;

/** Décalage évitant les clés négatives dans le mélange de coordonnées. */
const KEY_OFFSET = 1_000_000;
const KEY_STRIDE = 4_000_000;

export interface SpatialIndex {
  /**
   * Retourne un sur-ensemble des indices de points situés dans le rayon.
   * Les indices sont rendus par ordre croissant.
   */
  readonly query: (position: Coordinate, radiusMeters: number) => readonly number[];
  readonly cellCount: number;
  readonly pointCount: number;
}

export function buildSpatialIndex(track: ComputedTrack): SpatialIndex {
  let minLat = Number.POSITIVE_INFINITY;
  let maxLat = Number.NEGATIVE_INFINITY;
  let minLon = Number.POSITIVE_INFINITY;
  let maxLon = Number.NEGATIVE_INFINITY;

  for (let i = 0; i < track.count; i += 1) {
    const lat = track.lat[i];
    const lon = track.lon[i];
    if (lat === undefined || lon === undefined) continue;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lon < minLon) minLon = lon;
    if (lon > maxLon) maxLon = lon;
  }

  const centreLat = track.count > 0 ? (minLat + maxLat) / 2 : 0;
  const centreLon = track.count > 0 ? (minLon + maxLon) / 2 : 0;
  const cosCentre = Math.max(0.01, Math.cos(centreLat * DEG_TO_RAD));

  const toCellX = (lon: number): number =>
    Math.floor((normalizeDelta(lon - centreLon) * cosCentre * METERS_PER_DEGREE) / CELL_SIZE_METERS);
  const toCellY = (lat: number): number =>
    Math.floor(((lat - centreLat) * METERS_PER_DEGREE) / CELL_SIZE_METERS);

  const cells = new Map<number, number[]>();
  for (let i = 0; i < track.count; i += 1) {
    const lat = track.lat[i];
    const lon = track.lon[i];
    if (lat === undefined || lon === undefined) continue;
    const key = cellKey(toCellX(lon), toCellY(lat));
    const bucket = cells.get(key);
    if (bucket === undefined) cells.set(key, [i]);
    else bucket.push(i);
  }

  const query = (position: Coordinate, radiusMeters: number): readonly number[] => {
    const reach = Math.ceil(radiusMeters / CELL_SIZE_METERS) + CELL_MARGIN;
    const centreX = toCellX(position.lon);
    const centreY = toCellY(position.lat);
    const found: number[] = [];
    for (let x = centreX - reach; x <= centreX + reach; x += 1) {
      for (let y = centreY - reach; y <= centreY + reach; y += 1) {
        const bucket = cells.get(cellKey(x, y));
        if (bucket === undefined) continue;
        for (const index of bucket) found.push(index);
      }
    }
    found.sort((a, b) => a - b);
    return found;
  };

  return { query, cellCount: cells.size, pointCount: track.count };
}

function cellKey(x: number, y: number): number {
  return (x + KEY_OFFSET) * KEY_STRIDE + (y + KEY_OFFSET);
}

function normalizeDelta(deltaDegrees: number): number {
  let d = deltaDegrees;
  while (d > 180) d -= 360;
  while (d <= -180) d += 360;
  return d;
}
